import {
  Box,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue,
  Flex,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Heading,
  Tag,
  WrapItem,
  Wrap,
} from "@chakra-ui/react";
import { PrivateNavbar } from "./layout/PrivateNavbar";
import { ChatDrawer } from "./chat/ChatDrawer";
import { ChatNavbar } from "./chat/ChatNavbar";
import { useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { userAPI } from "services/user-api";
import { useChatStore } from "stores/chatStore";
import { ChatSessionType } from "types";
import {
  FaComments,
  FaUser,
  FaRobot,
  FaUsers,
  FaCreditCard,
  FaDollarSign,
  FaDiscord,
  FaUserSecret,
  FaCubes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  FiColumns,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiLifeBuoy,
  FiList,
  FiMail,
  FiMessageSquare,
  FiPhoneCall,
  FiPlus,
  FiSettings,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { Footer } from "components/layout/Footer";
export const Dashboard = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [credits, setCredits] = React.useState<number>(0);

  const navigate = useNavigate();
  const {
    currentSession,
    sessions,
    loadSessions,
    createSession,
    deleteSession,
    updateSessionName,
    input,
    promptImage,
    lastPromptImage,
    setPromptImage,
    setLastPromptImage,
    promptImageFile,
    setPromptImageFile,
    setInput,
  } = useChatStore();
  const { colorMode, toggleColorMode } = useColorMode();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(
    null
  );
  const [sessionNameInput, setSessionNameInput] = React.useState<string>("");
  const cardBg = useColorModeValue("white", "gray.800");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await userAPI.getCurrentUser();
        setCredits(Number(userData.credits));
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };
    loadUserData();
    // if (!isMobile) {
    //   onOpen();
    // }
  }, []);
  const handleSelectSession = async (selectedSessionId: string) => {
    await navigate(`/chat/${selectedSessionId}`);
  };
  const handleStartRename = (
    session: { id: string; sessionName?: string },
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setSessionNameInput(session.sessionName || "");
  };
  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSessionId && sessionNameInput.trim()) {
      await updateSessionName(editingSessionId, sessionNameInput.trim());
      setEditingSessionId(null);
      setSessionNameInput("");
    }
  };
  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteSession(id);
  };
  const handleNewChat = async (
    sessionType: ChatSessionType = ChatSessionType.STANDARD
  ) => {
    const session = await createSession(sessionType);
    if (session) {
      navigate(`/chat/${session.id}`);
    }
    loadSessions();
  };

  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <ChatNavbar
        onOpen={onOpen}
        navigate={navigate}
        credits={credits}
        colorMode={colorMode}
        borderColor={borderColor}
      />
      <ChatDrawer
        isOpen={isOpen}
        onClose={onClose}
        sessions={sessions}
        handleNewChat={handleNewChat}
        handleSelectSession={handleSelectSession}
        handleRename={handleRename}
        handleDeleteSession={handleDeleteSession}
        editingSessionId={editingSessionId}
        sessionNameInput={sessionNameInput}
        setSessionNameInput={setSessionNameInput}
        useColorModeValue={useColorModeValue}
        setEditingSessionId={setEditingSessionId}
        handleStartRename={handleStartRename}
        currentSession={currentSession}
        // handleNewCharacter={handleNewCharacter}
      />
      {/* Dashboard Content */}
      <Flex flex={1} p={8} align="center" justify="center" bg={bgColor} mt={10}>
        <Box w="full">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {/* Chat Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon
                  as={FaComments}
                  boxSize={8}
                  color="blue.500"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: "scale(1.1)" }}
                />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.chat.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.chat.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiMessageSquare />}
                      onClick={() => navigate("/chat")}
                      colorScheme="gray"
                    >
                      {t("dashboard.chat.chatInterface")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() => handleNewChat(ChatSessionType.STANDARD)}
                      colorScheme="gray"
                    >
                      {t("dashboard.chat.basicChat")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() =>
                        handleNewChat(ChatSessionType.COMPARATIVE_CHAT)
                      }
                      colorScheme="gray"
                    >
                      {t("dashboard.chat.compareChat")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Agent Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon
                  as={FaCubes}
                  boxSize={8}
                  color="green.500"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: "scale(1.1)" }}
                />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.agent.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.agent.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiList />}
                      onClick={() => navigate("/agents")}
                      colorScheme="gray"
                    >
                      {t("dashboard.agent.viewAgents")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() => navigate("/agents?new=1")}
                      colorScheme="gray"
                    >
                      {t("dashboard.agent.newAgent")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Character Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon
                  as={FaUserSecret}
                  boxSize={8}
                  color="orange.500"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: "scale(1.1)" }}
                />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.character.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.character.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiList />}
                      onClick={() => navigate("/characters")}
                      colorScheme="gray"
                    >
                      {t("dashboard.character.viewCharacters")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() => navigate("/characters?new=1")}
                      colorScheme="gray"
                    >
                      {t("dashboard.character.newCharacter")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Users Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon
                  as={FaUsers}
                  boxSize={8}
                  color="sandybrown"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: "scale(1.1)" }}
                />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.users.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.users.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiList />}
                      onClick={() => navigate("/subusers")}
                      colorScheme="gray"
                    >
                      {t("dashboard.users.viewUsers")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() => navigate("/subusers?new=1")}
                      colorScheme="gray"
                    >
                      {t("dashboard.users.newUser")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Credits Card     */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon as={FaDollarSign} boxSize={8} color="green.500" />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.credits.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.credits.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiList />}
                      onClick={() => navigate("/payments")}
                      colorScheme="gray"
                    >
                      {t("dashboard.credits.viewPayments")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPlus />}
                      onClick={() => navigate("/payments/add-funds")}
                      colorScheme="gray"
                    >
                      {t("dashboard.credits.addCredits")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiFileText />}
                      onClick={() => navigate("/usage")}
                      colorScheme="gray"
                    >
                      {t("dashboard.credits.viewUsage")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Settings Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon as={FiSettings} boxSize={8} color="purple.500" />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.settings.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.settings.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiUser />}
                      onClick={() => navigate("/profile")}
                      colorScheme="gray"
                    >
                      {t("dashboard.settings.profile")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiSettings />}
                      onClick={() => navigate("/settings")}
                      colorScheme="gray"
                    >
                      {t("dashboard.settings.settings")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiUsers />}
                      onClick={() => navigate("/affiliate")}
                      colorScheme="gray"
                    >
                      {t("dashboard.settings.affiliate")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>

            {/* Community Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon as={FiUsers} boxSize={8} color="yellow.500" />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.community.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.community.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiGrid />}
                      onClick={() => navigate("/features")}
                      colorScheme="gray"
                    >
                      {t("dashboard.community.viewFeatures")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FaDiscord />}
                      onClick={() => {
                        window.open("https://discord.gg/yzAWk3Zt", "_blank");
                      }}
                      colorScheme="gray"
                    >
                      {t("dashboard.community.viewDiscord")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>
            {/* Support Card */}
            <Box
              p={6}
              borderRadius="lg"
              boxShadow="md"
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "lg",
                opacity: 0.95,
              }}
            >
              <HStack spacing={4} align="flex-start">
                <Icon as={FiLifeBuoy} boxSize={8} color="blue.500" />
                <VStack align="start" flex={1}>
                  <Heading size="md">{t("dashboard.support.title")}</Heading>
                  <Text color={useColorModeValue("gray.600", "gray.300")}>
                    {t("dashboard.support.description")}
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiPhoneCall />}
                      onClick={() => navigate("/support")}
                      colorScheme="gray"
                    >
                      {t("dashboard.support.viewSupport")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiMail />}
                      onClick={() => navigate("/contact")}
                      colorScheme="gray"
                    >
                      {t("dashboard.support.viewContact")}
                    </Button>
                  </Wrap>
                </VStack>
              </HStack>
            </Box>
          </SimpleGrid>
        </Box>
      </Flex>
      <Footer />
    </Box>
  );
};
